import React, { useState, useEffect } from 'react';
// Native MUI Components
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, Edit, Check, Close } from '@mui/icons-material';
import { Traveller, Trip, Apartment } from '@/types';
import { travellerService } from '@/services/travellerService';
import EditTravellerForm from './EditTravellerForm';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import { calculateAge } from '@/utils/calculations';
import { useI18n } from '@/hooks/useI18n';

interface TravellerTableSimpleProps {
  trip: Trip;
  travellers: Traveller[];
  apartments: Apartment[];
}

interface MultiEditData {
  apartmentId?: string;
  skiPassType?: string;
  transportationType?: string;
}

const TravellerTableSimple: React.FC<TravellerTableSimpleProps> = ({
  trip,
  travellers,
  apartments,
}) => {
  const { skiPasses, transportationOptions } = trip;
  const [selectedTravellers, setSelectedTravellers] = useState<Set<string>>(new Set());
  const [multiEditMode, setMultiEditMode] = useState(false);
  const [multiEditData, setMultiEditData] = useState<MultiEditData>({});
  const { t } = useI18n();

  const getApartmentName = (apartmentId?: string) => {
    const apartment = apartments.find(apt => apt.id === apartmentId);
    return apartment ? apartment.name : t('traveller.table.notAssigned');
  };

  const getSkiPassName = (skiPassId?: string) => {
    const skiPass = skiPasses.find(pass => pass.id === skiPassId);
    return skiPass ? skiPass.name : t('traveller.table.notSelected');
  };

  const getTransportationName = (transportationId?: string) => {
    const transportation = transportationOptions.find(t => t.id === transportationId);
    return transportation ? transportation.name : t('traveller.table.notSelected');
  };

  const toggleTravellerSelection = (travellerId: string) => {
    const newSelection = new Set(selectedTravellers);
    if (newSelection.has(travellerId)) {
      newSelection.delete(travellerId);
    } else {
      newSelection.add(travellerId);
    }
    setSelectedTravellers(newSelection);
  };

  const selectAllTravellers = () => {
    if (selectedTravellers.size === travellers.length) {
      setSelectedTravellers(new Set());
    } else {
      setSelectedTravellers(new Set(travellers.map(t => t.id)));
    }
  };

  const applyMultiEdit = async () => {
    const updates = Array.from(selectedTravellers).map(travellerId => {
      const traveller = travellers.find(t => t.id === travellerId);
      if (traveller) {
        const updatedTraveller: Partial<Traveller> = {};

        if (multiEditData.apartmentId !== undefined) {
          updatedTraveller.apartmentId = multiEditData.apartmentId === 'none' ? undefined : multiEditData.apartmentId;
        }
        if (multiEditData.skiPassType !== undefined) {
          updatedTraveller.skiPassType = multiEditData.skiPassType === 'none' ? undefined : multiEditData.skiPassType;
        }
        if (multiEditData.transportationType !== undefined) {
          updatedTraveller.transportationType = multiEditData.transportationType === 'none' ? undefined : multiEditData.transportationType;
        }

        if (Object.keys(updatedTraveller).length > 0) {
          return travellerService.updateTraveller(trip.id, traveller.id, updatedTraveller);
        }
      }
      return Promise.resolve();
    });

    await Promise.all(updates);

    setMultiEditMode(false);
    setSelectedTravellers(new Set());
    setMultiEditData({});
  };

  const cancelMultiEdit = () => {
    setMultiEditMode(false);
    setSelectedTravellers(new Set());
    setMultiEditData({});
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedTravellers.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('traveller.table.selectedCount', { count: selectedTravellers.size })}
              </Typography>
              {!multiEditMode ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setMultiEditMode(true)}
                  startIcon={<Edit />}
                  sx={{ height: 32 }}
                >
                  {t('traveller.table.multiEdit')}
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    onClick={applyMultiEdit} 
                    startIcon={<Check />}
                    sx={{ height: 32 }}
                  >
                    {t('traveller.table.applyChanges')}
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="secondary" 
                    onClick={cancelMultiEdit} 
                    startIcon={<Close />}
                    sx={{ height: 32 }}
                  >
                    {t('traveller.table.cancel')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {multiEditMode && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ backgroundColor: 'primary.lighter' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              ✏️ {t('traveller.table.multiEditSelectedTravellers')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🏠 {t('traveller.table.apartment')}</InputLabel>
                  <Select
                    value={multiEditData.apartmentId || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, apartmentId: e.target.value })}
                    label={`🏠 ${t('traveller.table.apartment')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noApartment')}</MenuItem>
                    {apartments.map((apartment) => (
                      <MenuItem key={apartment.id} value={apartment.id}>
                        {apartment.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🎿 {t('traveller.table.skiPass')}</InputLabel>
                  <Select
                    value={multiEditData.skiPassType || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, skiPassType: e.target.value })}
                    label={`🎿 ${t('traveller.table.skiPass')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noSkiPass')}</MenuItem>
                    {skiPasses.map((skiPass) => (
                      <MenuItem key={skiPass.id} value={skiPass.id}>
                        {skiPass.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🚌 {t('traveller.table.transportation')}</InputLabel>
                  <Select
                    value={multiEditData.transportationType || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, transportationType: e.target.value })}
                    label={`🚌 ${t('traveller.table.transportation')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noTransportation')}</MenuItem>
                    {transportationOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ width: 48 }}>
                <IconButton
                  onClick={selectAllTravellers}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  {selectedTravellers.size === travellers.length ?
                    <CheckBox color="primary" /> :
                    <CheckBoxOutlineBlank />
                  }
                </IconButton>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>👤 {t('traveller.table.name')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>📧 {t('traveller.table.email')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>🎂 {t('traveller.table.age')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>⛷️ {t('traveller.table.skiClub')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>🏠 {t('traveller.table.apartment')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>🎿 {t('traveller.table.skiPass')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>🚌 {t('traveller.table.transportation')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('traveller.table.actions')}</TableCell>
            </TableRow>
          </TableHead>          <TableBody>
            {travellers.map((traveller) => (
              <TableRow
                key={traveller.id}
                sx={{ 
                  '&:hover': { backgroundColor: 'grey.50' },
                  backgroundColor: selectedTravellers.has(traveller.id) ? 'primary.lighter' : 'transparent'
                }}
              >
                <TableCell>
                  <IconButton
                    onClick={() => toggleTravellerSelection(traveller.id)}
                    size="small"
                    sx={{ p: 0.5 }}
                  >
                    {selectedTravellers.has(traveller.id) ?
                      <CheckBox color="primary" /> :
                      <CheckBoxOutlineBlank />
                    }
                  </IconButton>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {traveller.name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {traveller.email}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {t('traveller.table.ageYears', { age: calculateAge(traveller.dateOfBirth) })}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <MemberStatusChip isMember={traveller.isSkiClubMember} />
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {getApartmentName(traveller.apartmentId)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {getSkiPassName(traveller.skiPassType)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {getTransportationName(traveller.transportationType)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <EditTravellerForm 
                    traveller={traveller} 
                    trip={trip}
                    trigger={
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TravellerTableSimple;
